/*
PURPOSE: Shared editorial shell for the auth / transitional surfaces (login,
magic-link verify, request-login, sign-out, unsubscribe, welcome).
USE CASES:
- A calm, centred column on the warm ivory ground with a soft, breathing coral
  HexBloom behind it — the brand gesture from the migrated public pages
  (mirrors the Thank-you page's decorative bloom), so these surfaces stop
  looking like the old coral-reef screens.
DOMAIN BOUNDARIES:
- Presentation only. The bloom is decorative (aria-hidden, pointer-events-none)
  and fixed-seed, not data-tied. Motion is gated behind motion-safe so it
  respects prefers-reduced-motion. Content is passed as children.
*/
import React from 'react';
import HexBloom from '../HexBloom/HexBloom';

interface EditorialAuthShellProps {
  /** Fixed bloom seed so the mark is stable per surface. */
  bloomSeed?: number;
  children: React.ReactNode;
}

const EditorialAuthShell: React.FC<EditorialAuthShellProps> = ({
  bloomSeed = 4,
  children,
}) => {
  return (
    <div className="relative bg-ivory min-h-screen flex flex-col items-center justify-center px-6 py-16 overflow-hidden">
      {/* Decorative, breathing bloom behind the content — fixed seed, not
          data-tied. Soft and low-opacity so it reads as a warm ground, not a
          focal point. */}
      <div
        aria-hidden="true"
        className="absolute left-1/2 -translate-x-1/2 top-[14vh] pointer-events-none select-none opacity-50"
      >
        <HexBloom seed={bloomSeed} size={380} className="motion-safe:animate-breathe" />
      </div>

      <div className="relative z-10 w-full max-w-md text-center">
        {children}
      </div>
    </div>
  );
};

export default EditorialAuthShell;
